import axios from 'axios';

export const API_URL = import.meta.env.VITE_API_URL || '/api';

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

// Attach the stored JWT as a Bearer token (in addition to the HttpOnly cookie).
// This lets a cross-app deep-link launch (Command Center → here) authenticate via
// the launch token saved in localStorage, even when no taskops cookie is present.
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  // Ngrok free interstitials HTML-intercept API calls without this header.
  if (/ngrok/i.test(String(config.baseURL || API_URL || ''))) {
    config.headers['ngrok-skip-browser-warning'] = 'true';
  }
  // Let the browser set multipart boundary for FormData uploads.
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    if (config.headers && typeof config.headers.delete === 'function') {
      config.headers.delete('Content-Type');
    } else if (config.headers) {
      delete config.headers['Content-Type'];
      delete config.headers['content-type'];
    }
  }
  return config;
});

/**
 * Silent-refresh coordinator.
 *
 * Multi-account life gets ugly if two failed requests kick off two refreshes
 * at once: both rotate the same refresh token, one wins on the server, the
 * other lands on a revoked token and forces a bogus re-login. So the first
 * 401 for an expired token starts ONE refresh; every other request that
 * meets a 401 during that window awaits the same promise.
 */
let refreshInFlight = null;
async function doSilentRefresh() {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    // Lazy require — authService imports this module, don't circle at load time.
    const { default: authService } = await import('./authService');
    try {
      await authService.refresh(); // uses cookie or active account's saved token
      return true;
    } catch {
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

/**
 * Return response.data directly; normalize errors to { code, message }.
 *
 * ── `fullResponse`, and the one thing it is for ────────────────────────────
 *
 * Unwrapping to `.data` is right for every JSON call and wrong for a file
 * download, where the server also sends the FILENAME in `Content-Disposition`
 * and the unwrap throws it away. Every download in the app therefore rebuilt a
 * name in the browser, and the Market Intel export proved what that costs: the
 * server was naming the workbook after the client and the browser was saving it
 * as `market-intel-7fd4d4.xlsx`, because the two names were written in two
 * places and only one of them was ever used.
 *
 * Opt-in per request, so nothing that does not ask for it changes.
 */
api.interceptors.response.use(
  (response) => (response.config?.fullResponse ? response : response.data),
  async (error) => {
    const status = error.response?.status;
    const cfg = error.config || {};
    const url = String(cfg.url || '');
    const isAuthCall = url.includes('/auth/');
    // Blob downloads (responseType:'blob') receive error bodies as Blobs too —
    // parse them so the user sees the backend's message, not "NETWORK_ERROR".
    let data = error.response?.data;
    if (typeof Blob !== 'undefined' && data instanceof Blob) {
      try {
        data = JSON.parse(await data.text());
      } catch {
        data = null;
      }
    }
    const payload = data?.error || {
      code: 'NETWORK_ERROR',
      message: error.message || 'Network error',
    };

    // Try silent refresh exactly once per request for token-expired 401s on
    // non-auth endpoints. `_skipAuthRefresh` on the request opts out entirely
    // (used by authService.refresh itself to avoid infinite recursion).
    const refreshableCodes = new Set(['TOKEN_EXPIRED', 'INVALID_TOKEN', 'NO_TOKEN']);
    if (
      status === 401 &&
      !isAuthCall &&
      !cfg._skipAuthRefresh &&
      !cfg._retried &&
      refreshableCodes.has(payload.code)
    ) {
      const ok = await doSilentRefresh();
      if (ok) {
        cfg._retried = true;
        try {
          // Retry the original request with the new access token attached
          // by the request interceptor.
          const retryRes = await api(cfg);
          return retryRes;
        } catch (retryErr) {
          return Promise.reject(retryErr);
        }
      }
    }

    // Session expired — but NOT upstream service-key failures (502 + DENTALIQ_/CC_ codes).
    const upstreamCodes = new Set([
      'DENTALIQ_UNAUTHORIZED', 'DENTALIQ_IP_NOT_ALLOWED', 'DENTALIQ_FORBIDDEN',
      'DENTALIQ_DISABLED', 'DENTALIQ_UNREACHABLE', 'DENTALIQ_NOT_CONFIGURED',
      'CC_UNAUTHORIZED', 'CC_NOT_CONFIGURED', 'CC_UNREACHABLE', 'CC_PUSH_FAILED', 'CC_ARCHIVE_FAILED',
      'DCC_PUSH_FAILED', 'BAD_GATEWAY',
    ]);
    if (status === 401 && !isAuthCall && !upstreamCodes.has(payload.code)) {
      /**
       * Carry where they were, INCLUDING the query string.
       *
       * A hard redirect to `/login` loses the route entirely — so a session that
       * expires while somebody is following a notification link (`/leads/qc?
       * clientId=…&requestId=…`) sends them to login and then to the dashboard,
       * with no trace of what they were asked to do. `?next=` is read by the login
       * page, which restores the full url after sign-in.
       */
      if (window.location.pathname !== '/login') {
        const back = `${window.location.pathname}${window.location.search}${window.location.hash}`;
        window.location.href = `/login?next=${encodeURIComponent(back)}`;
      }
    }
    return Promise.reject(payload);
  }
);

export default api;
